import { motion, useInView } from "framer-motion";
import { useRef, useEffect } from "react";

const NMC_SERVER = "https://agent.nevermissacustomer.ai";
const TENANT_ID = "sagar-luxury";

// Cache-busting for the embedded scripts.
//
// form.js and widget.js are served from the agent origin, which sits behind
// Cloudflare. With no version in the URL a deployed fix could stay invisible
// behind a stale copy for hours — a booking-form fix shipped and returning
// visitors kept running the broken build, which looks exactly like a fix that
// did not work.
//
// The two repos deploy independently, so this site cannot hard-code a build
// hash. It reads /asset-manifest.json instead (never cached, a few hundred
// bytes) and appends ?v=<hash>, which the agent serves as immutable. A deploy
// there changes the hash, the next visitor requests a URL no cache has seen,
// and the update is instant with no deploy here.
//
// Best-effort by design: if the manifest cannot be read we load the plain URL,
// which the agent serves as no-cache — correct, just one revalidation slower.
let manifestPromise = null;
function assetVersions() {
  if (!manifestPromise) {
    manifestPromise = fetch(`${NMC_SERVER}/asset-manifest.json`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return manifestPromise;
}

/** `${NMC_SERVER}/form.js?v=hash`, or the bare URL when the hash is unknown. */
function versioned(url, name, versions) {
  const v = versions && versions[name];
  if (!v) return url;
  return url + (url.includes("?") ? "&" : "?") + "v=" + encodeURIComponent(v);
}

function BookingForm() {
  useEffect(() => {
    let cancelled = false;
    // The guard matches on "form.js" as a substring, so it still holds once the
    // URL carries a ?v= query.
    if (document.querySelector('script[src*="form.js"]')) {
      if (window.__nmcFormInit) window.__nmcFormInit();
      return undefined;
    }
    assetVersions().then((versions) => {
      if (cancelled || document.querySelector('script[src*="form.js"]')) return;
      const script = document.createElement("script");
      script.src = versioned(`${NMC_SERVER}/form.js`, "form.js", versions);
      script.async = true;
      script.crossOrigin = "anonymous";
      script.onload = () => {
        if (window.__nmcFormInit) window.__nmcFormInit();
      };
      document.body.appendChild(script);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return <div data-nmc-form="" data-tenant={TENANT_ID} data-no-title="" />;
}

// Floating chat bubble — loaded once, persists across the entire site
function useChatBubble() {
  useEffect(() => {
    let cancelled = false;
    if (document.querySelector('script[src*="widget.js"]')) return undefined;
    assetVersions().then((versions) => {
      if (cancelled || document.querySelector('script[src*="widget.js"]')) return;
      const script = document.createElement("script");
      // widget.js reads its config off its OWN script tag's query string, so the
      // version is appended to the existing params rather than replacing them.
      script.src = versioned(
        `${NMC_SERVER}/widget.js?tenant=${TENANT_ID}&server=${encodeURIComponent(NMC_SERVER)}&theme=dark&color=%23C9A84C&bubble_label=Chat%20with%20us`,
        "widget.js",
        versions
      );
      script.async = true;
      script.crossOrigin = "anonymous";
      document.body.appendChild(script);
    });
    return () => {
      cancelled = true;
    };
  }, []);
}

export default function BookingWidget() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  useChatBubble();

  return (
    <section
      id="booking"
      className="min-h-screen flex items-center relative overflow-hidden"
      style={{ background: "#111111" }}
    >
      {/* Ambient gold glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%)",
        }}
      />

      <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 w-full py-20">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-10"
        >
          <p className="text-gold tracking-[0.3em] text-xs uppercase mb-4 font-medium">
            Available 24/7
          </p>
          <h2 className="section-heading">Book Your Ride</h2>
          <div className="gold-divider" />
          <p className="text-cream/55 mt-4 text-sm md:text-base leading-relaxed">
            Select your vehicle, pick a time, and confirm — or chat with our AI
            agent anytime using the button in the corner.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.75, delay: 0.15 }}
        >
          <div
            className="border border-gold/20 overflow-hidden"
            style={{ background: "#0a0a0a" }}
          >
            <BookingForm />
          </div>
        </motion.div>

        <p className="text-center text-cream/25 text-xs tracking-wider mt-5">
          Powered by{" "}
          <span className="text-gold/40">Never Miss A Chat</span>
        </p>
      </div>
    </section>
  );
}
